import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { MeResponse } from '../../api';
import { MoneyOrUnavailable } from '../../components/MoneyOrUnavailable';

/**
 * The risk line at the top of Home's open positions card (specs/P10-risk-at-stop/spec.md R6–R8,
 * §4; Proposal, to be validated): the total risk at stop loss of the FXMate positions, and how
 * many have no stop loss and are left out of that total. The count of open positions is the
 * section title's; today's loss stays the status card's figure (M1 R9), not repeated here.
 * Nothing is shown without an open position.
 */
export function RiskSummary({
  openPositions,
}: {
  readonly openPositions: MeResponse['openPositions'];
}): ReactElement | null {
  const { t } = useTranslation();
  if (openPositions.count === 0) return null;
  return (
    // C3 R7: on a narrow screen the count without a stop wraps under the total, never over it.
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        columnGap: 2,
        rowGap: 0.5,
        px: 1.75,
        py: 1.5,
        borderBottom: `1px solid ${theme.fx.neutralBg}`,
      })}
    >
      <Box component="dl" sx={{ m: 0 }}>
        <Box
          component="dt"
          sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase' }}
        >
          {t('home.positions.risk.total')}
        </Box>
        <Box component="dd" sx={{ m: 0, fontSize: 15 }}>
          <MoneyOrUnavailable
            value={openPositions.riskAtStopTotal}
            unavailableLabel={t('home.positions.risk.unavailable')}
          />
        </Box>
      </Box>
      {openPositions.withoutStopCount > 0 && (
        <Typography sx={(theme) => ({ fontSize: 13, color: theme.fx.warningText })}>
          {t('home.positions.risk.withoutStop', { count: openPositions.withoutStopCount })}
        </Typography>
      )}
    </Box>
  );
}
